import { useEffect, useState } from 'react';
import { useRouteContext } from '@tanstack/react-router';
import {
	hasUsercentricsServiceConsent,
	onUsercentricsConsentChanged,
	onUsercentricsReady,
	readStoredUsercentricsServiceConsent,
} from '@worldcoo/ui/lib/usercentrics';

/**
 * Consent for a service, including the state before the answer is known.
 *
 * `'unknown'` is a real state, not a placeholder for `'denied'`: the CMP is asked asynchronously, so it is
 * what the server renders and what the first client render must agree with. Callers need to distinguish it
 * because the two are visually different — "not yet asked" should look like nothing has happened, while
 * "refused" needs to explain itself. Collapsing them is what made every consented donor see a
 * click-to-load prompt appear and then disappear.
 */
export type ServiceConsent = 'unknown' | 'granted' | 'denied';

/**
 * Whether the donor has consented to a named CMP service, kept current as they change their mind.
 *
 * Starts `'unknown'` on both the server and the first client render, so the two agree and no third party
 * is contacted before the answer is in. It resolves in an effect — from the CMP's stored record
 * immediately, then from the CMP itself once it has loaded — and re-resolves whenever consent changes.
 */
export function useUsercentricsServiceConsent(serviceName: string): ServiceConsent {
	// An embed shows no banner: the integrator collected consent on the platform hosting the iframe, so a
	// second one would ask the donor to consent twice for one journey (see the `isInIframe` branch in
	// `__root.tsx`). That means there is no CMP in the page to ask and nothing that could ever change the
	// answer, so reading consent from it denied every service for the whole visit — every campaign video
	// degraded to click-to-load — while `grantEmbeddedConsent` granted Consent Mode storage two effects
	// away, on the very same premise. This is that premise applied to the other half of the decision.
	const { isInIframe, consentDecision } = useRouteContext({ from: '__root__' });
	// Seeded from the mirror cookie, which the server and the client both read — so this is not a guess the
	// client makes after the fact, it is the same answer the server rendered from, and the two agree by
	// construction. That is what removes the visible correction: a donor who already accepted gets the embed
	// in the SSR markup instead of a poster that turns into one. `undefined` stays `'unknown'`.
	const [consent, setConsent] = useState<ServiceConsent>(() =>
		consentDecision?.granted.includes(serviceName)
			? 'granted'
			: consentDecision?.denied.includes(serviceName)
				? 'denied'
				: 'unknown',
	);

	useEffect(() => {
		if (isInIframe) return;
		let active = true;

		const settle = (granted: boolean) => {
			if (active) setConsent(granted ? 'granted' : 'denied');
		};

		const askCmp = () => {
			hasUsercentricsServiceConsent(serviceName)
				.then(settle)
				.catch((error: unknown) => {
					console.error(new Error(`Failed to read Usercentrics consent for "${serviceName}": ${String(error)}`));
					// Fail closed: a read we could not complete must not be mistaken for permission.
					settle(false);
				});
		};

		// The stored record first, because it is the same answer about a second sooner than the CMP can give
		// it. Read here rather than in the `useState` initialiser on purpose: the server has no storage, so
		// seeding from it would make the first client render disagree with the markup it is hydrating —
		// React #418, the error this whole module is arranged to avoid.
		const stored = readStoredUsercentricsServiceConsent(serviceName);
		if (stored !== undefined) settle(stored);

		// Then the CMP itself, once it exists. Without this the answer never arrived at all for a returning
		// donor: `hasUsercentricsServiceConsent` reports `false` while the script is still in flight, and the
		// decision the CMP restores at init is not a change, so `UC_CONSENT_CHANGED` never corrects it.
		const stopWaiting = onUsercentricsReady((available) => {
			if (available) askCmp();
			// The CMP never arrived — blocked by an extension, or offline. The stored record is then the only
			// answer there will be; with neither, fail closed rather than leave the donor looking at a poster
			// with no way to play it.
			else if (stored === undefined) settle(false);
		});
		const unsubscribe = onUsercentricsConsentChanged(askCmp);

		return () => {
			active = false;
			stopWaiting();
			unsubscribe();
		};
	}, [serviceName, isInIframe]);

	return isInIframe ? 'granted' : consent;
}
